import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import cx from 'helpers/cx'

import { ArrowLeftLargeIcon } from '@pleio/icons/arrow-left-large'

import Button from 'js/components/Button'
import { DisplayDate } from 'js/components/DisplayDate'
import ConfirmAccessModal from 'js/components/EntityActions/components/ConfirmAccessModal'
import Flexer from 'js/components/Flexer/Flexer'
import { H4 } from 'js/components/Heading'
import IconButton from 'js/components/IconButton'
import DeleteForm from 'js/components/Item/ItemActions/components/DeleteForm'
import ItemActions from 'js/components/Item/ItemActions/ItemActions'
import ItemStatusTag from 'js/components/Item/ItemStatusTag'
import Modal from 'js/components/Modal/Modal'
import Sticky from 'js/components/Sticky/Sticky'
import Text from 'js/components/Text/Text'
import { PAGE_EDIT } from 'js/page/Edit.queries'
import { ADMIN_PAGES } from 'js/page/PageList.queries'

import PageEditorRevisions from '../PageEditorRevisions'
import SettingsModal from '../SettingsModal'

import styles from './PageEditorHeader.module.css'
import type { PageEditorHeaderProps } from './PageEditorHeader.types'

const REFETCH_QUERIES = [ADMIN_PAGES, PAGE_EDIT]

const PageEditorHeader = ({
    entity,
    site,
    viewer,
    revisionTitle,
    isEditingWidget,
    isHidden,
    isSubmitting,
    isArchived,
    isDirty,
    isPublished,
    isFooter,
    group,
    accessControl,
    setAccessControl,
    setWidgetEditing,
    onSaveDraft,
    onPublish,
    onDiscardChanges,
}: PageEditorHeaderProps) => {
    const { t } = useTranslation()
    const location = useLocation()

    const { title, timeUpdated, url } = entity

    const [showSettingsModal, setShowSettingsModal] = useState(false)
    const toggleSettingsModal = () => setShowSettingsModal(!showSettingsModal)

    const [showAccessModal, setShowAccessModal] = useState(false)

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal)

    const [discardChangesModal, setDiscardChangesModal] = useState(false)

    const handleAfterDelete = () => {
        window.location.href = '/admin/pages'
    }

    const discardChanges = () => {
        setWidgetEditing(null)
        onDiscardChanges()
        setDiscardChangesModal(false)
    }

    const goBackUrl = location?.state?.prevPathname || url

    const showItemsActions = !isFooter

    const buttonIsDisabled = isSubmitting || isEditingWidget

    return (
        <>
            <Sticky
                className={cx(
                    styles.header,
                    isEditingWidget && styles.isEditingWidget,
                    isHidden && styles.isHidden,
                )}
            >
                <IconButton
                    size="large"
                    variant="secondary"
                    as={Link}
                    to={goBackUrl}
                    tooltip={t('action.go-back')}
                    disabled={buttonIsDisabled}
                    data-testid="button-back"
                >
                    <ArrowLeftLargeIcon />
                </IconButton>
                <div>
                    <H4
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {title}
                        {!revisionTitle && (
                            <ItemStatusTag
                                entity={entity}
                                style={{ marginLeft: '4px' }}
                            />
                        )}
                    </H4>
                    {revisionTitle && (
                        <Text
                            size="tiny"
                            variant="grey"
                            style={{ marginBottom: '2px' }}
                        >
                            {t('version.version', { count: 1 })}:{' '}
                            {revisionTitle}
                        </Text>
                    )}
                    {timeUpdated && !revisionTitle && (
                        <Text
                            size="tiny"
                            variant="grey"
                            style={{ marginBottom: '2px' }}
                        >
                            {t('global.last-saved')}:{' '}
                            <DisplayDate
                                date={timeUpdated}
                                type="timeSince"
                                side="right"
                            />
                        </Text>
                    )}
                </div>
                <Flexer gutterSize="md" style={{ marginLeft: 'auto' }}>
                    {!isArchived && (
                        <Flexer>
                            {isDirty && (
                                <Button
                                    size="normal"
                                    variant="tertiary"
                                    onClick={() => setDiscardChangesModal(true)}
                                    disabled={buttonIsDisabled}
                                >
                                    {t('form.discard-changes')}
                                </Button>
                            )}
                            {!isPublished && (
                                <Button
                                    size="normal"
                                    variant="secondary"
                                    disabled={buttonIsDisabled || !isDirty}
                                    loading={isSubmitting}
                                    onClick={onSaveDraft}
                                    data-testid="button-save-draft"
                                >
                                    {t('action.save-draft')}
                                </Button>
                            )}
                            <Button
                                size="normal"
                                variant="primary"
                                disabled={
                                    buttonIsDisabled ||
                                    (isPublished && !isDirty)
                                }
                                loading={isSubmitting}
                                onClick={() => {
                                    if (isPublished) {
                                        onPublish()
                                    } else {
                                        setShowAccessModal(true)
                                    }
                                }}
                                data-testid="button-publish"
                            >
                                {isPublished
                                    ? isDirty
                                        ? t('action.save')
                                        : t('action.saved')
                                    : t('action.publish')}
                            </Button>
                        </Flexer>
                    )}

                    <Flexer divider dividerOffset="sm">
                        {showItemsActions && (
                            <ItemActions
                                entity={entity}
                                canDuplicate
                                canChangeAccess={isPublished}
                                onTogglePageSettings={toggleSettingsModal}
                                onAfterArchive={discardChanges}
                                disabled={buttonIsDisabled}
                                refetchQueries={REFETCH_QUERIES}
                            />
                        )}
                        <PageEditorRevisions disabled={buttonIsDisabled} />
                    </Flexer>
                </Flexer>
            </Sticky>

            <SettingsModal
                isVisible={showSettingsModal}
                onClose={toggleSettingsModal}
                entity={entity}
                site={site}
                viewer={viewer}
            />

            <ConfirmAccessModal
                showModal={showAccessModal}
                onConfirm={() => {
                    setShowAccessModal(false)
                    onPublish()
                }}
                onCancel={() => setShowAccessModal(false)}
                accessControl={accessControl}
                setAccessControl={setAccessControl}
                group={group}
            />

            <Modal
                isVisible={showDeleteModal}
                onClose={toggleDeleteModal}
                title={t('entity-cms.delete')}
                size="small"
            >
                <DeleteForm
                    entity={entity}
                    onClose={toggleDeleteModal}
                    afterDelete={handleAfterDelete}
                    refetchQueries={['PageList']}
                />
            </Modal>

            <Modal
                size="small"
                isVisible={discardChangesModal}
                title={t('form.discard-changes')}
                onClose={() => setDiscardChangesModal(false)}
                data-testid="modal-discard-changes"
            >
                {t('form.discard-changes-confirm')}
                <Flexer mt>
                    <Button
                        size="normal"
                        variant="tertiary"
                        onClick={() => setDiscardChangesModal(false)}
                        data-testid="button-discard-cancel"
                    >
                        {t('action.cancel')}
                    </Button>
                    <Button
                        size="normal"
                        variant="warn"
                        onClick={discardChanges}
                        data-testid="button-discard-confirm"
                    >
                        {t('form.discard-changes')}
                    </Button>
                </Flexer>
            </Modal>
        </>
    )
}

export default PageEditorHeader
